import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Coins,
  Handshake,
  Loader2,
  Star,
  Zap,
  LayoutDashboard,
  HelpCircle,
  ShieldCheck,
  Flame,
  Award,
  Crown,
  Layers,
  Feather,
  Clapperboard,
  Target,
  Headphones,
  Sliders,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AudienceUseCaseIcon } from '@/components/audience/AudienceUseCaseIcon';
import { CppShowcaseVideos } from '@/components/partners/CppShowcaseVideos';
import {
  creativePartnerBenefits,
  creativePartnerRoleOptions,
  creativePartnerSegments,
  creativePartnerSteps,
} from '@/data/creativePartnerSegments';
import {
  fetchMyCreativePartnerApplication,
  submitCreativePartnerApplication,
} from '@/lib/creativePartnershipApi';
import { formatApiError } from '@/lib/formatApiError';
import { leftTopArrowGradient, rightBottomArrowGradient } from '@/assets/svgs/gradientIcons';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';

import { TwinneeLogo } from '@/components/TwinneeLogo';
function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('storycraft_user') || '{}');
  } catch {
    return {};
  }
}

function getSegmentIcon(iconType) {
  switch (iconType) {
    case 'crown':
      return <Crown className="w-5 h-5" />;
    case 'layers':
      return <Layers className="w-5 h-5" />;
    case 'feather':
      return <Feather className="w-5 h-5" />;
    case 'clapperboard':
      return <Clapperboard className="w-5 h-5" />;
    case 'target':
      return <Target className="w-5 h-5" />;
    case 'headphones':
      return <Headphones className="w-5 h-5" />;
    case 'sliders':
      return <Sliders className="w-5 h-5" />;
    default:
      return <TwinneeLogo className="w-5 h-5" />;
  }
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  }),
};

export function CreativePartnershipPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('storycraft_token');
  const storedUser = useMemo(() => readStoredUser(), []);

  const [existingApp, setExistingApp] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(Boolean(token));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [fullName, setFullName] = useState(storedUser.name || storedUser.full_name || '');
  const [email, setEmail] = useState(storedUser.email || '');
  const [primaryRole, setPrimaryRole] = useState('');
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [audienceReach, setAudienceReach] = useState('');
  const [tools, setTools] = useState('');
  const [pitch, setPitch] = useState('');
  const [consent, setConsent] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!token) {
      setLoadingStatus(false);
      return;
    }
    setLoadingStatus(true);
    try {
      const data = await fetchMyCreativePartnerApplication();
      setExistingApp(data?.application || null);
    } catch {
      /* guest or stale token */
    } finally {
      setLoadingStatus(false);
    }
  }, [token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const scrollToForm = () => {
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!primaryRole) {
      toast.error('Select your primary role');
      return;
    }
    if (!consent) {
      toast.error('Please agree to be contacted about the program');
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitCreativePartnerApplication({
        full_name: fullName.trim(),
        email: email.trim(),
        primary_role: primaryRole,
        portfolio_url: portfolioUrl.trim(),
        audience_reach: audienceReach.trim() || undefined,
        tools: tools.trim() || undefined,
        pitch: pitch.trim() || undefined,
        consent: true,
      });
      setSubmitted(true);
      setExistingApp(result?.application || null);
      toast.success('Application received — we will be in touch soon.');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not submit application'));
    } finally {
      setSubmitting(false);
    }
  };

  const showForm = !existingApp && !submitted;

  return (
    <div
      className="min-h-screen w-full bg-black text-white font-sora relative overflow-x-hidden selection:bg-[#2DD4BF]/30 flex flex-col justify-between"
      style={{ isolation: 'isolate' }}
      data-testid="creative-partnership-page"
    >
      {/* ── Ambient glows pinned to corners (Studio Theme) ── */}
      <div
        className="fixed top-0 left-0 w-[700px] lg:w-[900px] pointer-events-none z-[1] select-none"
        style={{ opacity: 0.60, transform: 'translate(-20%, -20%)' }}
        dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
      />
      <div
        className="fixed bottom-0 right-0 w-[600px] lg:w-[800px] pointer-events-none z-[1] select-none"
        style={{ opacity: 0.60, transform: 'translate(20%, 20%)' }}
        dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }}
      />

      {/* ─────────────────────────────────────────
          Top Navigation Bar (Studio Header)
      ───────────────────────────────────────── */}
      <header className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-5 pb-3 flex items-center justify-between gap-2 flex-wrap border-b border-white/5 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="h-8 px-3.5 rounded-xl border border-white/10 text-white hover:bg-white/[0.06] hover:text-[#62FAE3] text-xs font-bold flex items-center gap-2 cursor-pointer backdrop-blur-md bg-white/[0.02] transition-all shadow-sm font-jakarta"
            title="Return to Dashboard"
          >
            <LayoutDashboard className="w-4 h-4 text-[#62FAE3]" />
            <span>Dashboard</span>
          </button>

          <Link
            to="/"
            className="hidden sm:flex items-center hover:opacity-90 transition-opacity cursor-pointer"
            title="Twinnee Home"
          >
            <TwinneeWordmark className="h-8" />
          </Link>
        </div>

        <div className="flex items-center gap-3 font-jakarta">
          <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-[#2DD4BF]/10 border border-[#2DD4BF]/25 text-[#62FAE3] text-xs font-mono font-bold">
            <Handshake className="w-3.5 h-3.5" />
            <span>CREATIVE PARTNER PROGRAM</span>
          </div>

          <button
            onClick={scrollToForm}
            className="h-8 px-4 rounded-xl bg-gradient-to-r from-[#8B5CF6] via-[#6366F1] to-[#2DD4BF] text-white hover:opacity-95 text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-all shadow-md"
          >
            <span>Apply Now</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* ─────────────────────────────────────────
          Main Hero & Content Container
      ───────────────────────────────────────── */}
      <main className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-14 flex-1">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto text-center space-y-4 mb-14 sm:mb-20">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="text-[11px] font-mono font-medium tracking-[0.18em] text-[#2DD4BF] uppercase mb-3">
              CREATIVE PARTNERSHIP PROGRAM
            </p>

            <h1 className="text-3xl sm:text-5xl lg:text-[54px] font-black tracking-tight text-white leading-[1.15]">
              Scale Your Narrative Studio
              <span
                className="block mt-1 sm:mt-2"
                style={{
                  background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                with Free AI Credits & VIP Access
              </span>
            </h1>

            <p className="text-sm sm:text-base text-[#9CA3AF] font-normal max-w-2xl mx-auto font-jakarta leading-relaxed mt-4">
              For influencers, storytellers, AI filmmakers, ad creators, podcasters, and editors who ship narrative content with AI — partner with us for monthly credit grants, early model access, and global showcase distribution.
            </p>

            {/* Quick CTAs & Stat Pills */}
            <div className="flex flex-wrap items-center justify-center gap-3 pt-6 font-jakarta">
              <button
                type="button"
                onClick={scrollToForm}
                className="px-8 py-3 rounded-full font-bold text-xs sm:text-sm tracking-wide bg-gradient-to-r from-[#8B5CF6] via-[#6366F1] to-[#2DD4BF] text-white hover:opacity-95 shadow-[0_0_25px_rgba(98,250,227,0.3)] transition-all cursor-pointer flex items-center gap-2"
                data-testid="partners-hero-cta"
              >
                <span>Apply for Partnership</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className="px-6 py-3 rounded-full font-bold text-xs sm:text-sm text-gray-200 hover:text-white bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 transition-all cursor-pointer"
              >
                <span>View Standard Plans</span>
              </button>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-10 font-jakarta max-w-3xl mx-auto text-left">
              <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-[#62FAE3]" />
                </div>
                <div>
                  <h4 className="text-xs font-bold font-sora text-white">Monthly Credit Pool</h4>
                  <p className="text-[11px] text-[#9CA3AF] mt-0.5">Complimentary credits granted every month.</p>
                </div>
              </div>

              <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#A78BFA]/15 border border-[#A78BFA]/30 flex items-center justify-center shrink-0">
                  <Flame className="w-4 h-4 text-[#D0BCFF]" />
                </div>
                <div>
                  <h4 className="text-xs font-bold font-sora text-white">VIP Model Access</h4>
                  <p className="text-[11px] text-[#9CA3AF] mt-0.5">Early preview of Runway Gen-3 & Google Veo.</p>
                </div>
              </div>

              <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <Award className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-xs font-bold font-sora text-white">Co-Marketing & Reach</h4>
                  <p className="text-[11px] text-[#9CA3AF] mt-0.5">Showcase on Twinnee socials & homepage.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ─────────────────────────────────────────
            Cinematic Video Showcase
        ───────────────────────────────────────── */}
        <CppShowcaseVideos />

        {/* ─────────────────────────────────────────
            Who We Partner With (Segments Grid)
        ───────────────────────────────────────── */}
        <section className="py-16 sm:py-24 border-t border-white/[0.06] relative z-10">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-12 sm:mb-16 space-y-3"
            >
              <p className="text-[11px] font-mono font-medium tracking-[0.2em] text-[#2DD4BF] uppercase">
                PARTNER ECOSYSTEM
              </p>
              <h2 className="font-sora font-black text-3xl sm:text-4xl lg:text-[42px] text-white tracking-tight leading-tight">
                Who We Partner With
              </h2>
              <p className="text-sm sm:text-base text-[#9CA3AF] font-jakarta leading-relaxed max-w-2xl mx-auto">
                Whether you produce high-velocity short-form reels or full cinematic episodes.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 font-jakarta">
              {creativePartnerSegments.map((item, i) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="group relative flex flex-col justify-between rounded-[22px] border border-white/[0.08] bg-[#0b0f17]/90 hover:bg-[#0f1624] hover:border-[#2DD4BF]/50 transition-all duration-300 p-5 backdrop-blur-xl shadow-xl hover:-translate-y-1.5 hover:shadow-[0_0_35px_rgba(45,212,191,0.14)] overflow-hidden"
                  data-testid={`partner-segment-${item.id}`}
                >
                  {/* Top Accent Light Highlight */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#2DD4BF]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                  {/* Ambient Colored Radial Lighting */}
                  <div
                    className="absolute -top-10 -right-10 w-28 h-28 rounded-full pointer-events-none blur-2xl opacity-10 group-hover:opacity-25 transition-opacity duration-300"
                    style={{ background: item.accent }}
                  />

                  <div>
                    {/* Card Header: Custom Icon + Badge */}
                    <div className="flex items-center justify-between gap-2 mb-3.5">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center border shadow-inner transition-transform group-hover:scale-105 duration-200"
                        style={{
                          borderColor: `${item.accent}33`,
                          background: `${item.accent}14`,
                          color: item.accent,
                        }}
                      >
                        {getSegmentIcon(item.iconType)}
                      </div>

                      <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border border-white/10 text-gray-300 bg-white/[0.03] group-hover:border-[#2DD4BF]/30 group-hover:text-[#62FAE3] transition-colors">
                        {item.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-sora font-black text-lg text-white tracking-tight group-hover:text-[#62FAE3] transition-colors mb-2">
                      {item.title}
                    </h3>

                    {/* Body */}
                    <p className="text-xs sm:text-[13px] text-[#9CA3AF] font-jakarta leading-relaxed">
                      {item.body}
                    </p>
                  </div>

                  {/* Bottom Highlight Tag */}
                  <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[11px] font-mono text-gray-400 group-hover:text-[#62FAE3] transition-colors">
                      <Zap className="w-3 h-3 text-[#2DD4BF] shrink-0" />
                      <span className="truncate">{item.perk}</span>
                    </div>
                  </div>
                </motion.article>
              ))}

              {/* 8th Interactive Card: Custom Pipeline */}
              <motion.article
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 7 * 0.04 }}
                onClick={scrollToForm}
                className="group relative flex flex-col justify-between rounded-[22px] border border-dashed border-[#A78BFA]/30 bg-[#0b0f17]/60 hover:bg-[#121124] hover:border-[#A78BFA] transition-all duration-300 p-5 backdrop-blur-xl shadow-xl hover:-translate-y-1.5 cursor-pointer overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#A78BFA]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                <div>
                  <div className="flex items-center justify-between gap-2 mb-3.5">
                    <div className="w-11 h-11 rounded-xl bg-[#A78BFA]/15 border border-[#A78BFA]/30 flex items-center justify-center text-[#D0BCFF] shadow-inner transition-transform group-hover:scale-105">
                      <TwinneeLogo className="w-5 h-5" />
                    </div>

                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border border-[#A78BFA]/30 text-[#D0BCFF] bg-[#A78BFA]/10">
                      CUSTOM FLOW
                    </span>
                  </div>

                  <h3 className="font-sora font-black text-lg text-white tracking-tight group-hover:text-[#D0BCFF] transition-colors mb-2">
                    Your Format?
                  </h3>

                  <p className="text-xs sm:text-[13px] text-[#9CA3AF] font-jakarta leading-relaxed">
                    Building a bespoke studio, agency, or custom multimodal pipeline? Tell us what models and workflows you need.
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between text-[11px] font-mono font-bold text-[#D0BCFF]">
                  <span>Apply with Custom Stack</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        {/* ─────────────────────────────────────────
            Studio Perks & Showcase Benefits (2 Cols)
        ───────────────────────────────────────── */}
        <section className="py-16 sm:py-20 border-t border-white/[0.06] relative z-10 font-jakarta">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-6 sm:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-[24px] border border-[#2DD4BF]/30 bg-[#2DD4BF]/[0.03] backdrop-blur-xl p-6 sm:p-8 shadow-xl relative overflow-hidden"
            >
              <div className="flex items-center gap-2 text-[#62FAE3] font-bold text-xs font-mono uppercase tracking-wider mb-4">
                <Coins className="w-4 h-4 text-[#2DD4BF]" />
                STUDIO PRODUCTION PERKS
              </div>
              <h3 className="text-xl font-bold font-sora text-white mb-4">
                Uncapped Creation Resources
              </h3>
              <ul className="space-y-3.5">
                {creativePartnerBenefits.studioPerks.map((line) => (
                  <li key={line} className="flex items-start gap-3 text-xs sm:text-sm text-gray-300">
                    <div className="w-4 h-4 rounded-full bg-[#2DD4BF]/20 border border-[#2DD4BF]/40 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-2.5 h-2.5 text-[#62FAE3] stroke-[3]" />
                    </div>
                    <span className="leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-[24px] border border-white/10 bg-white/[0.02] backdrop-blur-xl p-6 sm:p-8 shadow-xl relative overflow-hidden"
            >
              <div className="flex items-center gap-2 text-[#D0BCFF] font-bold text-xs font-mono uppercase tracking-wider mb-4">
                <Star className="w-4 h-4 text-[#A78BFA]" />
                SHOWCASE & CO-CREATION
              </div>
              <h3 className="text-xl font-bold font-sora text-white mb-4">
                Audience Growth & Spotlight
              </h3>
              <ul className="space-y-3.5">
                {creativePartnerBenefits.showcase.map((line) => (
                  <li key={line} className="flex items-start gap-3 text-xs sm:text-sm text-gray-300">
                    <div className="w-4 h-4 rounded-full bg-[#A78BFA]/20 border border-[#A78BFA]/40 flex items-center justify-center shrink-0 mt-0.5">
                      <TwinneeLogo className="w-2.5 h-2.5 text-[#D0BCFF]" />
                    </div>
                    <span className="leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* ─────────────────────────────────────────
            How It Works (3 Steps)
        ───────────────────────────────────────── */}
        <section className="py-16 sm:py-20 border-t border-white/[0.06] relative z-10 font-jakarta">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 space-y-2">
              <p className="text-[11px] font-mono font-medium tracking-[0.18em] text-[#2DD4BF] uppercase">
                ONBOARDING ROADMAP
              </p>
              <h2 className="font-sora font-black text-2xl sm:text-3xl text-white tracking-tight">
                How Partnership Works
              </h2>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              {creativePartnerSteps.map((step) => (
                <div
                  key={step.n}
                  className="rounded-[22px] border border-white/10 bg-white/[0.02] backdrop-blur-xl p-6 shadow-xl relative overflow-hidden"
                >
                  <p className="text-[#62FAE3] font-mono font-bold text-sm mb-2">
                    STEP {step.n}
                  </p>
                  <h3 className="font-sora font-bold text-base text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-xs text-[#9CA3AF] leading-relaxed">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─────────────────────────────────────────
            Application Form / Status Box
        ───────────────────────────────────────── */}
        <section id="apply" className="py-16 sm:py-20 border-t border-white/[0.06] relative z-10 font-jakarta">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8 space-y-2">
              <p className="text-[11px] font-mono font-medium tracking-[0.18em] text-[#2DD4BF] uppercase">
                PARTNER APPLICATION
              </p>
              <h2 className="font-sora font-black text-2xl sm:text-3xl text-white tracking-tight">
                Apply to the Program
              </h2>
              <p className="text-xs text-[#9CA3AF]">
                Our team reviews creator applications within 5–7 business days.
              </p>
            </div>

            {loadingStatus ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#2DD4BF]" />
                <span className="text-xs font-mono text-[#9CA3AF]">Checking application status...</span>
              </div>
            ) : existingApp ? (
              <div
                className="rounded-[24px] border border-emerald-500/30 bg-emerald-500/[0.06] backdrop-blur-xl p-8 text-center shadow-xl"
                data-testid="partner-application-status"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-sora font-bold text-xl text-white mb-2">
                  Application on File
                </h3>
                <p className="text-xs text-gray-300 mb-2">
                  Current Status:{' '}
                  <span className="text-[#62FAE3] font-bold font-mono uppercase">
                    {existingApp.status}
                  </span>
                </p>
                <p className="text-[11px] text-[#9CA3AF] font-mono">
                  Submitted {existingApp.created_at ? new Date(existingApp.created_at).toLocaleDateString() : 'recently'}
                </p>
              </div>
            ) : submitted ? (
              <div className="rounded-[24px] border border-emerald-500/30 bg-emerald-500/[0.06] backdrop-blur-xl p-8 text-center shadow-xl">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-sora font-bold text-xl text-white mb-2">Thank you!</h3>
                <p className="text-xs text-[#9CA3AF]">
                  Your application has been received. Our partnerships lead will reach out via email once review is complete.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-4 rounded-[24px] border border-white/10 bg-white/[0.02] backdrop-blur-xl p-6 sm:p-8 shadow-2xl"
                data-testid="partner-application-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="full_name" className="text-xs font-bold text-gray-300">
                    Full Name *
                  </Label>
                  <Input
                    id="full_name"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-10 rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] text-white text-xs font-jakarta"
                    placeholder="Your full name"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-bold text-gray-300">
                    Work Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-10 rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] text-white text-xs font-jakarta"
                    placeholder="you@domain.com"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-gray-300">Primary Role *</Label>
                  <Select value={primaryRole} onValueChange={setPrimaryRole} required>
                    <SelectTrigger
                      className="h-10 rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] text-white text-xs font-jakarta"
                      data-testid="partner-role-select"
                    >
                      <SelectValue placeholder="Select your primary creative role" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0e15] border border-white/15 text-white">
                      {creativePartnerRoleOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-xs hover:bg-white/10">
                          <span className="flex items-center gap-2">
                            <AudienceUseCaseIcon useCaseId={opt.audienceUseCaseId} size="sm" />
                            {opt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="portfolio_url" className="text-xs font-bold text-gray-300">
                    Portfolio / Social Profile Link *
                  </Label>
                  <Input
                    id="portfolio_url"
                    type="url"
                    required
                    placeholder="https://youtube.com/@channel or https://instagram.com/profile"
                    value={portfolioUrl}
                    onChange={(e) => setPortfolioUrl(e.target.value)}
                    className="h-10 rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] text-white text-xs font-jakarta"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="audience_reach" className="text-xs font-bold text-gray-300">
                    Audience Reach / Subscriber Count (Optional)
                  </Label>
                  <Input
                    id="audience_reach"
                    placeholder="e.g. 50k subscribers on YouTube, 20k on IG"
                    value={audienceReach}
                    onChange={(e) => setAudienceReach(e.target.value)}
                    className="h-10 rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] text-white text-xs font-jakarta"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="tools" className="text-xs font-bold text-gray-300">
                    Tools in Your Stack (Optional)
                  </Label>
                  <Input
                    id="tools"
                    placeholder="Twinnee, Runway, Premiere Pro, DaVinci, Midjourney…"
                    value={tools}
                    onChange={(e) => setTools(e.target.value)}
                    className="h-10 rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] text-white text-xs font-jakarta"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="pitch" className="text-xs font-bold text-gray-300">
                    Why Partner With Twinnee? (Optional)
                  </Label>
                  <Textarea
                    id="pitch"
                    maxLength={500}
                    rows={3}
                    value={pitch}
                    onChange={(e) => setPitch(e.target.value)}
                    className="rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] text-white text-xs font-jakarta resize-none"
                    placeholder="Tell us about the narrative content you're creating and what you'd like to build together..."
                  />
                </div>

                <div className="flex items-start gap-2.5 pt-1">
                  <Checkbox
                    id="consent"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(Boolean(v))}
                    className="mt-0.5 border-white/30 data-[state=checked]:bg-[#2DD4BF] data-[state=checked]:text-black"
                  />
                  <Label htmlFor="consent" className="text-[11px] text-[#9CA3AF] leading-relaxed cursor-pointer">
                    I agree to be contacted about the Twinnee Creative Partnership Program and understand that my application will be reviewed by the partnership team.
                  </Label>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !showForm}
                  className="w-full h-11 rounded-full font-bold text-xs tracking-wide bg-gradient-to-r from-[#8B5CF6] via-[#6366F1] to-[#2DD4BF] text-white hover:opacity-95 shadow-[0_0_20px_rgba(98,250,227,0.3)] transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="partner-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Submitting Application...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Partner Application</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      {/* ─────────────────────────────────────────
          Studio Footer
      ───────────────────────────────────────── */}
      <footer className="border-t border-white/[0.06] py-6 relative z-10 font-jakarta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#9CA3AF]">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1.5">
            <span>← Back to Home</span>
          </Link>
          <p>© {new Date().getFullYear()} Twinnee. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default CreativePartnershipPage;
